import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type { Conversation, ConversationId, Message, Page } from '@cyphex/types';
import { CHAT_KEYS } from '../constants';
import { previewText } from './markdown/flatten';

export type ConversationPages = InfiniteData<Page<Conversation>, string | undefined>;

/**
 * Applies `patch` to one conversation in EVERY cached list page and in its
 * detail entry, in a single pass and without invalidating anything — a badge
 * change must not trigger a refetch of the whole list.
 */
export function patchConversationEverywhere(
  client: QueryClient,
  conversationId: ConversationId,
  patch: (conversation: Conversation) => Conversation,
): void {
  client.setQueriesData<ConversationPages>({ queryKey: ['chat', 'conversations'] }, (data) => {
    if (!data) return data;
    let touched = false;
    const pages = data.pages.map((page) => ({
      ...page,
      items: page.items.map((conversation) => {
        if (conversation.id !== conversationId) return conversation;
        touched = true;
        return patch(conversation);
      }),
    }));
    return touched ? { ...data, pages } : data;
  });
  client.setQueryData<Conversation>(CHAT_KEYS.conversation(conversationId), (conversation) =>
    conversation ? patch(conversation) : conversation,
  );
}

/** Moves a conversation to the top of the list and refreshes its preview row. */
export function bumpConversation(
  client: QueryClient,
  conversationId: ConversationId,
  message: Message,
  isOwn: boolean,
): void {
  patchConversationEverywhere(client, conversationId, (conversation) => ({
    ...conversation,
    lastSeq: Math.max(conversation.lastSeq, message.seq),
    lastMessageAt: message.createdAt,
    lastMessage: {
      messageId: message.id,
      seq: message.seq,
      senderId: message.sender?.id ?? null,
      kind: message.kind,
      text: previewText(message.bodyText ?? ''),
    },
    self: isOwn
      ? { ...conversation.self, lastReadSeq: message.seq, unreadCount: 0, firstUnreadSeq: null }
      : {
          ...conversation.self,
          unreadCount: conversation.self.unreadCount + 1,
          firstUnreadSeq: conversation.self.firstUnreadSeq ?? message.seq,
        },
  }));
}

export function applyConversationPatch(
  client: QueryClient,
  conversationId: ConversationId,
  patch: Partial<Conversation>,
): void {
  patchConversationEverywhere(client, conversationId, (conversation) => ({ ...conversation, ...patch }));
}

/**
 * Patch only the caller's own slice (draft, unread, pin…).
 *
 * `applyConversationPatch` merges SHALLOWLY, so passing a partial `self` there
 * would silently drop every field it did not mention. This rebuilds `self` from
 * the current value instead, which is what every per-user flag needs.
 */
export function patchSelfState(
  client: QueryClient,
  conversationId: ConversationId,
  patch: Partial<Conversation['self']>,
): void {
  patchConversationEverywhere(client, conversationId, (conversation) => ({
    ...conversation,
    self: { ...conversation.self, ...patch },
  }));
}
